<template>
  <div>
    <modal @close="$emit('close')">
      <template slot="header">
        <div class="absolute top-0 left-0 z-40 pl-2 pr-2 mt-2 ml-2 ">
          <h3
            v-if="!isPdfOnly"
            class="object-contain mt-2 text-xs text-center text-orange-500 "
          >
            {{ printerHelperState }}
          </h3>
          <h3
            v-if="isPdfOnly"
            class="object-contain mt-2 text-xs text-center text-orange-500 "
          >
            Free tier pdf only
          </h3>
        </div>
        <img src="../assets/images/printer.svg" alt="printer" />
      </template>
      <template slot="body">
        <div
          v-for="label in uniqueLabels"
          :key="`${label.labelId}--${componentKey}`"
          class="flex justify-between px-4 mb-2 text-white rounded bg-primary-light"
        >
          <h4 class="flex-1 mr-4 text-left">
            {{ label.name ? label.name : label.labelName }}
          </h4>
          <drop-down
            class="flex-1"
            v-if="!isPdfOnly"
            placeholder="Select printer"
            :value="getSelectedPrinter(label.labelId)"
            value-property="name"
            search-prop="name"
            :options="assignedPrinters"
            @input="setPrinter(label, $event)"
          />
        </div>
      </template>
      <template slot="buttons">
        <g-button
          v-if="!isPdfOnly && isPrinterHelperAvailable"
          class="mt-4 primary"
          @click="print"
        >
          Print labels
        </g-button>

        <div
          v-if="!isPrinterHelperAvailable && !isPdfOnly"
          class="flex justify-center"
        >
          <p class="text-red-600 text-1xl">
            Where is the print button?
            <span class="flex"
              >The Cloudlabel agent seem to be disconnected or you are behind a
              proxy. Try to clear the cache and reload the page.
            </span>
            <br />
            <span class="flex justify-center"
              >Go to the docs and read this section:
            </span>
            <a
              class=" text-primary-light"
              href="https://www.cloudlabel.com/documentation/"
              target="_blank"
              >Cloudlabel docs</a
            >
          </p>
        </div>
        <g-button
          :disabled="!isPrintAllowed"
          v-if="isPdfEnabled"
          class="mt-4 ml-4 primary"
          @click="generatePdf"
        >
          Print to PDF
        </g-button>
        <g-button
          :disabled="sendingToGroup"
          v-if="enablePrintToGroup"
          class="mt-4 ml-4 primary"
          @click="sendToGroup"
        >
          Send to Group
        </g-button>
      </template>
    </modal>
    <printing-progress
      v-if="printing"
      heading="Printing"
      :phase="printProgress.phase"
      :total="printProgress.total"
      :current="printProgress.current"
      :successes="printProgress.successes"
      :failures="printProgress.failures"
      :currentLabel="printProgress.currentLabel"
    />
    <modal
      v-if="error"
      @close="error = false"
      type="error"
      heading="Error"
      :description="errorText"
    />
    <modal
      v-if="printingComplete"
      @close="closePrintingComplete"
      :type="printProgress.failures > 0 ? 'warning' : 'success'"
      heading="Printing complete"
      :description="printResultDescription"
    >
      <template slot="body">
        <h3>{{ printProgress.failures > 0 ? 'Printing completed with errors' : 'Printing complete' }}</h3>
        <p class="m-0">{{ printResultDescription }}</p>
        <div v-if="printProgress.failures > 0" class="mt-4 text-orange-600">
          <p class="font-semibold">{{ printProgress.failures }} label(s) failed to print.</p>
          <p class="text-sm">Check your printer connection and try again.</p>
        </div>
      </template>
    </modal>
  </div>
</template>
<script>
import Modal from "./Modal";
import DropDown from "./forms/DropDown";
import GButton from "./forms/Button";
import PrintingProgress from "./PrintingProgress";
import { forEachAsync } from "../utils/asyncHelpers";
import { sendToPrinterService } from "../utils/printerHelpers";

export default {
  components: {
    Modal,
    DropDown,
    GButton,
    PrintingProgress,
  },
  data: () => ({
    printerSelect: {},
    componentKey: 0,
    printing: false,
    error: false,
    errorText: "",
    printingComplete: false,
    previewImages: [],
    hasPresetPrinter: false,
    printProgress: {
      phase: "generating",
      total: 0,
      current: 0,
      successes: 0,
      failures: 0,
      currentLabel: ""
    },
    sendingToGroup: false,
  }),
  props: {
    labels: {
      type: Array,
      required: true,
    },
    printers: {
      type: Array,
      required: true,
    },
    presetprinter: {
      type: String,
      default: null,
    },
    autoprint: {
      type: Boolean,
      default: false,
    },
    order: {
      type: Object,
      default: undefined,
    },
    connector: {
      type: String,
      required: false
    },
    enablePrintToGroup: {
      type: Boolean,
      default: false,
    }
  },
  computed: {
    printerHelperHealth() {
      return this.$store.getters["SETTINGS/PRINTER_HELPER_HEALTH"];
    },
    printerHelperStatus() {
      var health = this.printerHelperHealth;
      return (health && health["data"]) || (health && health["error"]);
    },
    printerHelperState() {
      var health = this.printerHelperHealth;
      return health && health["data"] == "Healthy"
        ? "Printer is connected"
        : "No connection";
    },
    isPrinterHelperAvailable() {
      var health = this.printerHelperHealth;
      return health && health["data"] == "Healthy" ? true : false;
    },
    uniqueLabels() {
      return this.labels.filter((obj, pos, arr) => {
        return (
          arr.map(mapObj => mapObj["labelId"]).indexOf(obj["labelId"]) === pos
        );
      });
    },
    printQueues() {
      return this.$store.getters["SETTINGS/PRINT_QUEUES"];
    },
    assignedGroupLabels() {
      return this.$store.getters["LABELS/ASSIGNED_LABELS"];
    },
    assignedPrinters() {
      var assignedPrinters = this.assignedGroupLabels
        .flatMap(x => x.labels)
        .flatMap(x => x.printers)
        .flatMap(x => x.printerId);
      return this.printers.filter(p => {
        return assignedPrinters.find(x => x === p.id);
      });
    },
    isPdfEnabled() {
      const features = this.$store.getters["FEATURES/FEATURES"];
      if (features !== null) return features.pdfEnabled;
      return false;
    },
    isPdfOnly() {
      const features = this.$store.getters["FEATURES/FEATURES"];
      if (features !== null) return features.pdfOnly;
      return false;
    },
    printCounter() {
      return this.$store.getters["DATA/PRINTEDCOUNT"];
    },
    isPrintAllowed() {
      const { type } = this.$store.getters["SUBSCRIPTIONS/SUBSCRIPTION_DATA"];

      if (type !== "Free") return true;

      if (!this.printCounter) return false;

      const printCalc =
        this.printCounter.allowedPrintJobs - this.printCounter.printedJobs;

      return printCalc >= 0;
    },
    printResultDescription() {
      if (this.printProgress.total === 0) {
        return "Your labels have been processed.";
      }
      if (this.printProgress.failures === 0) {
        return `All ${this.printProgress.successes} label(s) have been successfully printed.`;
      }
      return `${this.printProgress.successes} of ${this.printProgress.total} label(s) printed successfully.`;
    },
  },
  async mounted() {
    await this.getData();
    this.uniqueLabels.forEach(label => {
      if (this.presetprinter) {
        const presetPrinter = this.assignedPrinters.find(
          p => p.id === this.presetprinter
        );
        if (presetPrinter) {
          this.printerSelect[label.labelId] = presetPrinter;
          this.hasPresetPrinter = true;
          return;
        }
      }

      if (this.assignedGroupLabels) {
        let defaultPrinters = this.assignedGroupLabels
          .flatMap(x => x.labels)
          .filter(l => l.labelId === label.labelId)
          .flatMap(x => x.printers)
          .filter(p => p.isDefault);
        const printer =
          defaultPrinters &&
          defaultPrinters.length &&
          this.assignedPrinters.find(
            printer => printer.id === defaultPrinters[0].printerId
          );
        if (printer) {
          this.printerSelect[label.labelId] = printer;
          this.hasPresetPrinter = true;
          return;
        }
      }

      if (label.defaultPrinter) {
        const printer = this.printers.find(
          printer => printer.id === label.defaultPrinter
        );
        if (printer) {
          this.printerSelect[label.labelId] = printer;
          this.hasPresetPrinter = true;
        }
      } else if (label.groups[0] && label.groups[0].defaultPrinterId) {
        const printer = this.printers.find(
          printer => printer.id === label.groups[0].defaultPrinterId
        );
        if (printer) {
          this.printerSelect[label.labelId] = printer;
          this.hasPresetPrinter = true;
        }
      }
    });

    await this.checkForAutoprint();

    this.componentKey++;
  },
  methods: {
    async getData() {
      if (this.$store.getters["FEATURES/FEATURES"] === null) {
        await this.$store.dispatch("FEATURES/GET_FEATURES");
      }
      let assignedGroupLabels = this.$store.getters["LABELS/ASSIGNED_LABELS"];
      if (assignedGroupLabels.length === 0) {
        await this.$store.dispatch("LABELS/GET_ASSIGNED_LABELS");
        assignedGroupLabels = this.$store.getters["LABELS/ASSIGNED_LABELS"];
      }
    },
    async checkForAutoprint() {
      if (this.autoprint === true && this.hasPresetPrinter === true)
        await this.print();
    },
    getSelectedPrinter(labelId) {
      return this.printerSelect[labelId] ? this.printerSelect[labelId] : {};
    },
    setPrinter(label, printer) {
      this.printerSelect[label.labelId] = printer;
      this.componentKey++;
    },
    openPdf(name, file) {
      const fileUrl = window.URL.createObjectURL(new Blob([file]));
      const fileLink = document.createElement("a");
      fileLink.target = "_blank";
      fileLink.href = fileUrl;
      fileLink.setAttribute("download", `${name}.pdf`);
      document.body.appendChild(fileLink);
      fileLink.click();
    },
    mapArticleDataToVariables(articleData = []) {
      return articleData.map(variable => ({
        Name: variable.key || "",
        Type: variable.propertyType || "Unknown",
        Value: variable.data,
      }));
    },
    buildGroupPrintEntries() {
      return this.labels
        .filter(label => label && label.article)
        .map(label => ({
          articleId: label.article.articleId || label.article.id || null,
          ExternalIdentifier: label.article.externalIdentifier || "",
          OrderReference:
            (this.order && this.order.orderReference) || "",
          Variables: this.mapArticleDataToVariables(label.article.articleData),
          Quantity: parseInt(label.quantity, 10) || 1,
          LabelIds: [label.labelId],
          TargetGroupId: null,
        }));
    },
    async sendToGroup() {
      if (!this.labels || this.labels.length === 0) {
        return;
      }

      const printJobEntries = this.buildGroupPrintEntries();

      if (printJobEntries.length === 0) {
        return;
      }

      const groupedEntriesByArticleId = printJobEntries.reduce((acc, entry) => {
        const articleId = entry.articleId;

        if (!articleId) {
          return acc;
        }

        if (!acc[articleId]) {
          acc[articleId] = [];
        }

        acc[articleId].push({
          ExternalIdentifier: entry.ExternalIdentifier,
          OrderReference: entry.OrderReference,
          Variables: entry.Variables,
          Quantity: entry.Quantity,
          LabelIds: entry.LabelIds,
          TargetGroupId: entry.TargetGroupId,
        });

        return acc;
      }, {});

      this.sendingToGroup = true;
      this.error = false;
      this.errorText = "";

      try {
        const articleIds = Object.keys(groupedEntriesByArticleId);

        if (articleIds.length === 0) {
          this.error = true;
          this.errorText = "Could not determine article id for group print";
          return;
        }

        await forEachAsync(articleIds, async articleId => {
          await this.$store.dispatch("PRINTJOBS/CREATE_TO_GROUP", {
            articleId: parseInt(articleId, 10),
            PrintJobEntries: groupedEntriesByArticleId[articleId],
          });
        });

        await this.$store.dispatch("DATA/CALCULATE_PRINT_COUNT");
        this.$emit("close");
      } catch (e) {
        this.error = true;
        this.errorText =
          (e && e.response && e.response.data && e.response.data.Message) ||
          "Could not send print job to group";
      } finally {
        this.sendingToGroup = false;
      }
    },
    async generatePdf() {
      if (this.order) {
        const payload = {
          orderId: this.order.orderId,
          connectorId: this.connector,
          orderData: this.order.orderData.filter(x => x.editable),
          labels: this.labels.map(l => {
            return {
              labelId: l.labelId,
              quantity: l.quantity,
              articleId: l.article.articleId,
              editableData: l.article.editableData,
              printerId:
                this.printerSelect[l.labelId] === undefined
                  ? null
                  : this.printerSelect[l.labelId].id,
            };
          }),
        };
        this.printing = true;

        try {
          const { data } = await this.$store.$api.post(
            "/api/printjobs/pdf",
            payload,
            {
              responseType: "blob",
              headers: {
                "Content-Disposition":
                  'attachment; filename="order.pdf"; filename*="order.pdf"',
              },
            }
          );

          this.$store.dispatch("DATA/CALCULATE_PRINT_COUNT");

          this.printing = false;
          this.openPdf(this.order.orderReference, data);
          await this.decreasePrintQuantity();
        } catch (e) {
          this.printing = false;
          this.error = true;
          var responseText = await e.response.data.text();
          this.errorText = JSON.parse(responseText).Message;

          return;
        }
      } else {
        this.printing = true;
        this.error = false;
        this.errorText = "";

        const payload = {
          labels: this.labels.map(l => {
            return {
              labelId: l.labelId,
              quantity: l.quantity,
              articleId: l.article.id,
              editableData: l.article.articleData,
              printerId:
                this.printerSelect[l.labelId] === undefined
                  ? null
                  : this.printerSelect[l.labelId].id,
            };
          }),
          connectorId: this.connector
        };

        try {
          const { data } = await this.$store.$api.post(
            "/api/printjobs/article/pdf",
            payload,
            {
              responseType: "blob",
              headers: {
                "Content-Disposition":
                  'attachment; filename="article.pdf"; filename*="article.pdf"',
              },
            }
          );

          this.$store.dispatch("DATA/CALCULATE_PRINT_COUNT");
          this.openPdf("Article", data);
          this.printing = false;
        } catch (e) {
          this.printing = false;
          this.error = true;
          responseText = await e.response.data.text();
          this.errorText = JSON.parse(responseText).Message;
        }
      }
    },
    async decreasePrintQuantity() {
      const decreaseQtyPayload = {
        orderId: this.order.orderId,
        quantityData: this.labels.map(l => {
          return {
            consumedQuantity: l.quantity,
            articleId: l.article.articleId,
          };
        }),
      };
      await this.$store.$api.post(
        "api/printJobs/article/quantity",
        decreaseQtyPayload
      );
    },
    resetPrintProgress() {
      this.printProgress = {
        phase: "generating",
        total: 0,
        current: 0,
        successes: 0,
        failures: 0,
        currentLabel: ""
      };
    },
    getLabelNameForPrintFile(printFile) {
      // Try to find the label name from our labels prop
      const label = this.labels.find(l => {
        // Match by printJobEntryId if available, otherwise by labelId
        return l.labelId && this.uniqueLabels.some(ul => ul.labelId === l.labelId);
      });
      if (label) {
        return label.name || label.labelName || `Label ${label.labelId}`;
      }
      return "";
    },
    closePrintingComplete() {
      this.printingComplete = false;
      this.resetPrintProgress();
    },
    async print() {
      this.previewImages = [];
      this.resetPrintProgress();
      let printJobId;

      try {
        this.printing = true;
        let printFiles = [];
        const printerHelperUri = this.$store.getters[
          "SETTINGS/PRINTER_HELPER_URI"
        ];

        if (!this.printQueues) {
          await this.$store.dispatch("SETTINGS/GET_PRINT_QUEUES");
        }

        const computerName = this.printQueues && this.printQueues.computerName;

        if (this.order) {
          const payload = {
            orderId: this.order.orderId,
            orderData: this.order.orderData.filter(x => x.editable),
            computerName: computerName,
            labels: this.labels.map(l => {
              return {
                labelId: l.labelId,
                quantity: l.quantity,
                articleId: l.article.articleId,
                editableData: l.article.editableData,
                printerId: this.printerSelect[l.labelId].id,
              };
            }),
          };

          try {
            const { data } = await this.$store.$api.post(
              "/api/printjobs/order",
              payload
            );

            printFiles = data.data;
            printJobId = data.entityId;
            await this.decreasePrintQuantity();
          } catch (e) {
            this.error = true;
            this.errorText = e.response.data.Message;
            return;
          }
        } else {
          this.error = false;
          this.errorText = "";

          const payload = {
            computerName: computerName,
            labels: this.labels.map(l => {
              return {
                labelId: l.labelId,
                quantity: l.quantity,
                articleId: l.article.id,
                editableData: l.article.articleData,
                printerId:
                  this.printerSelect[l.labelId] === undefined
                    ? null
                    : this.printerSelect[l.labelId].id,
              };
            }),
            connectorId: this.connector
          };

          try {
            const { data } = await this.$store.$api.post(
              "/api/printjobs/article",
              payload
            );
            printFiles = data.data.data;
            printJobId = data.data.entityId;
          } catch (e) {
            this.error = true;
            this.errorText = e.response.data.Message;
          }

          if (this.error) {
            return;
          }
        }

        printFiles.forEach(file => {
          this.previewImages.push({
            name: "Label",
            data: file.previewImage,
          });
        });

        // Switch to printing phase and initialize progress tracking
        this.printProgress.phase = "printing";
        this.printProgress.total = printFiles.length;
        this.printProgress.current = 0;
        this.printProgress.successes = 0;
        this.printProgress.failures = 0;

        // Collect status updates for batch processing
        const statusUpdates = [];

        await forEachAsync(printFiles, async (printFile, index) => {
          // Update current progress
          this.printProgress.current = index + 1;
          this.printProgress.currentLabel = this.getLabelNameForPrintFile(printFile);

          try {
            await sendToPrinterService(printerHelperUri, {
              targetAddress: printFile.targetIpAddress,
              port: printFile.targetPort,
              targetPrintQueue: printFile.targetPrintQueue,
              printerFileUrl: printFile.printFileName,
              useLocalPrinterDriver: printFile.useLocalPrinterDriver,
              pdfAsBase64String: printFile.pdfAsBase64String,
              orientation: printFile.orientation,
              scale: printFile.scale,
              side: printFile.side,
              paperSize: printFile.paperSize,
              customPaperSize: printFile.customPaperSize,
            });

            this.printProgress.successes++;
            statusUpdates.push({
              printJobEntryId: printFile.printJobEntryId,
              status: "Success"
            });
          } catch (error) {
            this.printProgress.failures++;
            statusUpdates.push({
              printJobEntryId: printFile.printJobEntryId,
              status: "Error",
              message: "Could not print using printerhelper"
            });
          }
        });

        // Batch update all print job entry statuses
        if (statusUpdates.length > 0) {
          try {
            await this.$store.dispatch("PRINTJOBS/UPDATE_PRINTJOBENTRY_STATUS_BATCH", {
              updates: statusUpdates
            });
          } catch (batchError) {
            console.error('[PrinterSelect] Batch status update error:', batchError);
          }
        }

        await this.$store.dispatch("PRINTJOBS/UPDATE_PRINTJOB_STATUS", {
          printJobId,
          status: "Processed",
        });

        this.printingComplete = true;
      } catch (error) {
        this.error = true;
        this.errorText = "An error occurred during printing";

        console.log(error);
      } finally {
        this.printing = false;
      }
    },
  },
};
</script>
